import React from "react"
import styled, { css, useTheme } from "styled-components"
import media from "@styles/media"
import PropTypes from "prop-types"
import Html from "@components/html"
import Grid from "@components/elements/grid"
import Div from "@components/elements/div"
import Image from "@components/image"
import navArrowIcon from "@images/menu/icon-nav-arrow-up.svg"
import Anchor from "@components/elements/anchor"
import Flex from "@components/elements/flex"
import { LinkType } from "@types/link-type"
import { motion } from "framer-motion"

const NavbarDropdownText = styled.span`
  width: max-content;
  display: flex;
  height: min-content;
  gap: 0.5rem;
  align-items: center;
  color: ${({ theme }) => theme.color.blue600};
  transition: color 0.2s ease;
`

const NavbarDropdownEntry = styled.div`
  width: 100%;
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  transition: background-color 0.2s ease, color 0.2s ease;
  border-radius: 0.25rem;
  padding-right: 0.25rem;

  img {
    transition: filter 0.2s ease;
  }

  ${props =>
    props.isTitle &&
    css`
      color: #2d3343;
      font-weight: 700;
      line-height: 1;
      gap: 1rem;
      margin-bottom: 0.5rem;
      font-size: 1rem !important;
    `}

  ${props =>
    props.hasLink &&
    css`
      &:hover {
        img {
          filter: brightness(110%);
        }
        ${NavbarDropdownText} {
          color: ${({ theme }) => theme.color.blue500};
        }
      }
    `}
`

export const NavbarLinkWrapper = styled.span`
  margin: 0;
  ${media.sm`
    width: 100%;
  `}
`

export const NavbarSeperator = styled.div`
  min-height: 1px;
  width: 100%;
  background: #ddd;
  margin: 1rem 0;
`

const NavbarDropdownDescription = styled.span`
  color: #21264f;
  line-height: 1.5;
  font-weight: 600;
  font-size: 1rem;
  min-width: 28ch;
  max-width: 50ch;

  p {
    margin-bottom: 0;
  }
`

export const NavbarVerticalLine = styled.div`
  height: 1px;
  grid-column: 1/-1;
  background: #ddd;
  margin: 1rem 0 1rem 0;
`

export const NavbarVerticalEntry = styled.div`
  display: grid;
  grid-column: 1/-1;
  justify-content: center;
  margin-top: 0;

  ${media.sm`
    grid-column: span 1;
  `}
`

export const NavbarDropdownColumn = styled.div`
  height: min-content !important;
  ${props =>
    props.vertical &&
    css`
      display: flex;
      gap: 1rem;
      justify-content: center;
      margin-top: 0;
      align-self: flex-end;
      ${NavbarDropdownEntry} {
        flex: 0;
      }
    `}

  ${media.sm`
    margin-top: 0;
  `}

  ${props =>
    props.mobile &&
    css`
      span {
        font-size: 1rem;
        font-weight: bold;
        line-height: 1;
        letter-spacing: 0.9px;
        color: #2d3343;
        display: flex;
        gap: 1rem;
        transition: color 0.2s ease;
        align-items: center;
        width: max-content;
        margin: 0 auto 2rem auto;
      }

      ${NavbarDropdownEntry}:hover {
        background-color: transparent;
      }
    `}
`

export const NavbarSpacer = styled.div`
  width: 1px;
  background: #ddd;
  margin: 0 2rem;
`

export const Caret = styled(motion.div)`
  position: absolute;
  height: 15px;
  width: 15px;
  bottom: -5px;
  margin-left: 30px;
  background-image: url(${navArrowIcon});
  background-size: contain;
  background-repeat: no-repeat;
  z-index: 1001;
  pointer-events: none;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
`

export const NavbarLinkText = styled.span`
  line-height: 1.7;
  color: #333333;
  box-sizing: border-box;
  cursor: "pointer";
  font-size: .9rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  padding: 18px 12px;
  transition: color 0.2s ease;

  svg {
    margin-top: 2px;
    margin-left: 6px;
    fill: #333;
    transition: transform 0.2s ease, fill 0.2s ease;
  }

  ${NavbarLinkWrapper}:hover & {
    color: ${({ theme }) => theme.color.blue400};
    ${Caret} {
      opacity: 1;
      transform: translateY(-1px);
    }
    svg {
      transform: rotateZ(180deg);
      fill: ${({ theme }) => theme.color.blue400} !important;
    }
  }
`

const NavbarDropdownLink = styled(Anchor)`
  display: flex;
  height: min-content;
  flex-flow: column;
  gap: 1rem;
  padding: 8px 6px;
`

const DropdownLink = ({ href, children, ...rest }) => (
  <NavbarDropdownLink
    color="#3D455C"
    hover="#293966"
    className="website-navigation-link"
    link={href}
    as={href ? null : "span"}
    {...rest}
  >
    {children}
  </NavbarDropdownLink>
)

DropdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
}

const BackgroundWrapper = styled.div`
  transition: filter 0.2s;
`

export const ProductLink = ({
  link,
  logo,
  background,
  description,
  links,
  linkColor,
}) => {
  const { color } = useTheme()

  return (
    <BackgroundWrapper>
      <Flex
        height="100%"
        padding="1.5rem"
        borderRadius="1rem"
        background={background ?? color.blueLight}
        minWidth="280px"
      >
        <Grid gap="0.75rem" justifyItems="left">
          <Anchor arrow={false} link={link}>
            <Div>{logo}</Div>
          </Anchor>

          {description && (
            <NavbarDropdownDescription>
              {typeof description === "string" ? (
                <Html html={description} />
              ) : (
                description
              )}
            </NavbarDropdownDescription>
          )}

          {links?.length > 0 && (
            <Flex gap="0.5rem" margin={description && "0.5rem 0 0"}>
              {links.map((link, i) => (
                <Anchor
                  color={linkColor ?? color.blue400}
                  hover={color.blue500}
                  lineHeight={1.3}
                  link={link.link}
                  key={`double-menu-links-${i}`}
                  target={"_self"}
                >
                  {link.name}
                </Anchor>
              ))}
            </Flex>
          )}
        </Grid>
      </Flex>
    </BackgroundWrapper>
  )
}

ProductLink.propTypes = {
  link: PropTypes.string.isRequired,
  logo: PropTypes.node.isRequired,
  background: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  links: PropTypes.array,
  linkColor: PropTypes.string,
}

export const BackgroundLink = ({
  arrow = true,
  link,
  icon,
  name,
  background,
  description,
  links,
  linkColor,
  newTab,
  minWidth,
}) => {
  const { color } = useTheme()

  return (
    <BackgroundWrapper>
      <Flex
        height="100%"
        padding="1.5rem"
        borderRadius="1rem"
        background={background ?? color.blueLight}
        minWidth={minWidth ?? "280px"}
      >
        <Grid gap="0.75rem" justifyItems="left">
          {icon && name && (
            <Div>
              <Image width={40} height={40} file={icon} alt={`${name} Icon`} />
            </Div>
          )}

          {name && (
            <Anchor
              fontSize="1rem"
              color={color.blue600}
              hover={color.blue500}
              arrow={arrow}
              link={link}
              target={newTab && "_blank"}
            >
              {name}
            </Anchor>
          )}

          {description && (
            <NavbarDropdownDescription>
              {typeof description === "string" ? (
                <Html html={description} />
              ) : (
                description
              )}
            </NavbarDropdownDescription>
          )}

          {links?.length > 0 && (
            <Flex gap="0.5rem" margin={description && "0.5rem 0 0"}>
              {links.map((link, i) => (
                <Anchor
                  color={linkColor ?? color.blue400}
                  hover={color.blue500}
                  lineHeight={1.3}
                  link={link.link}
                  key={`double-menu-links-${i}`}
                >
                  {link.name}
                </Anchor>
              ))}
            </Flex>
          )}
        </Grid>
      </Flex>
    </BackgroundWrapper>
  )
}

BackgroundLink.propTypes = {
  arrow: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.object,
  name: PropTypes.string,
  background: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  links: PropTypes.array,
  linkColor: PropTypes.string,
  newTab: PropTypes.bool,
  minWidth: PropTypes.string,
}

export const DropdownEntry = ({
  icon,
  link,
  name,
  title = false,
  newTab,
  image,
  ...rest
}) => {
  return (
    <NavbarDropdownEntry isTitle={title} hasLink={link} {...rest}>
      <DropdownLink title={name} href={link} target={newTab && "_blank"}>
        <NavbarDropdownText>
          {image ? (
            image
          ) : (
            <Image
              height={title ? 34 : 18}
              width={title ? 34 : 18}
              file={icon}
              alt={name}
            />
          )}
          {name}
        </NavbarDropdownText>
      </DropdownLink>
    </NavbarDropdownEntry>
  )
}

DropdownEntry.propTypes = {
  icon: PropTypes.object,
  link: PropTypes.string,
  name: PropTypes.string.isRequired,
  title: PropTypes.bool,
  newTab: PropTypes.bool,
}

export const NavbarVerticalContainer = styled.div`
  align-self: stretch;
  height: min-content;
  grid-column: 1/-1;
  ${NavbarDropdownColumn} {
    margin-bottom: 0 !important;
  }
`

const DropdownColumn = ({
  index,
  name,
  icon,
  link,
  links,
  vertical,
  hidden,
  mobile,
}) => {
  const firstSpacer = React.useMemo(() => index != 0 && index % 3 != 0, [index])
  return vertical ? (
    <NavbarVerticalContainer>
      {!mobile && <NavbarVerticalLine />}
      <NavbarDropdownColumn vertical={!mobile} as="ul">
        {links?.map(({ name, icon, link }, i) => (
          <DropdownEntry
            as="li"
            icon={icon}
            link={link}
            name={name}
            title
            key={`DropdownColumnTitle-${i}-${name}`}
          />
        ))}
      </NavbarDropdownColumn>
    </NavbarVerticalContainer>
  ) : (
    <>
      {firstSpacer && !mobile && <NavbarSpacer />}
      <NavbarDropdownColumn>
        {!hidden && <DropdownEntry icon={icon} link={link} name={name} title />}
        <NavbarDropdownColumn as="ul">
          {links?.map(({ name, icon, link }, i) => (
            <DropdownEntry
              as="li"
              icon={icon}
              link={link}
              name={name}
              key={`DropdownColumnEntry-${i}-${name}`}
            />
          ))}
        </NavbarDropdownColumn>
      </NavbarDropdownColumn>
    </>
  )
}

DropdownColumn.propTypes = {
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  icon: PropTypes.object,
  link: PropTypes.string,
  links: PropTypes.array.isRequired,
  vertical: PropTypes.bool,
  hidden: PropTypes.bool,
  mobile: PropTypes.bool,
}

const DropdownEntries = ({ parent, mobile }) => {
  const title = mobile ? "MenuMobileCategory" : "MenuDesktopCategory"
  return parent.categories?.map(
    ({ name, icon, link, links, vertical, description, hidden }, index) => {
      return description ? (
        <BackgroundLink
          key={`${title}-${index}`}
          name={name}
          icon={icon}
          link={link}
          description={description}
        />
      ) : (
        <DropdownColumn
          index={index}
          key={`${title}-${index}`}
          name={name}
          icon={icon}
          link={link}
          links={links}
          vertical={vertical}
          hidden={hidden}
          mobile={mobile}
        />
      )
    }
  )
}

DropdownEntries.propTypes = {
  parent: PropTypes.object.isRequired,
  mobile: PropTypes.bool,
}

const NotLast = ({ index, length }) => {
  const notLast = index + 1 != length
  return notLast && <NavbarSpacer />
}

NotLast.propTypes = {
  index: PropTypes.number.isRequired,
  length: PropTypes.number.isRequired,
}

const LinkEntries = ({ parent, mobile }) => {
  if (parent.display || mobile) {
    return parent.description ? (
      <BackgroundLink
        link={parent.link}
        icon={parent.icon}
        name={parent.name}
        description={parent.description}
      />
    ) : (
      <NavbarDropdownColumn mobile={mobile}>
        <DropdownEntry
          icon={parent.icon}
          link={parent.link}
          name={parent.name}
          description={parent.description}
          title
        />
      </NavbarDropdownColumn>
    )
  }
}

LinkEntries.propTypes = {
  parent: PropTypes.object.isRequired,
  mobile: PropTypes.bool,
}

export const NavbarDropdownMenu = styled.div`
  border: 1px solid #ddd;
  background-color: #fff;
  border-radius: 2rem;
  background-clip: padding-box;

  position: absolute;
  left: 50%;
  padding: 20px;
  margin-top: 8px;

  list-style: none;
  font-size: 1rem;
  text-align: left;
  opacity: 0;
  pointer-events: none;

  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  transform: translateX(-49.5%) translateY(0) scale(1);
  box-shadow: 0 15px 20px -10px rgba(32, 51, 70, 0.3),
    0 0px 30px 0px rgba(32, 51, 70, 0.2);

  ${NavbarLinkWrapper}:hover &, &:hover {
    opacity: 1;
    transform: translateX(-49.5%) translateY(-11px) scale(1);
    z-index: 1000;
    pointer-events: auto;
  }
`

export const SingleMenu = ({ links }) => {
  const { color } = useTheme()

  return links?.map((link, i) => (
    <Anchor
      color={color.blue400}
      hover={color.blue500}
      lineHeight={1.3}
      link={link.link}
      key={`double-menu-links-${i}`}
    >
      {link.name}
    </Anchor>
  ))
}

SingleMenu.propTypes = {
  links: PropTypes.arrayOf(LinkType),
}

export const DoubleMenu = ({ name, link, links, fullWidth }) => {
  const { color } = useTheme()

  return (
    <Div>
      <Anchor
        fontSize="1rem"
        color={color.blue600}
        hover={color.blue500}
        arrow
        link={link}
      >
        {name}
      </Anchor>
      {links && (
        <Grid
          margin="1rem 0 0"
          gap="1rem 1rem"
          columns={fullWidth ? "1fr 1fr" : "repeat(2, 200px)"}
        >
          <SingleMenu links={links} />
        </Grid>
      )}
    </Div>
  )
}

DoubleMenu.propTypes = {
  name: PropTypes.string,
  link: PropTypes.string,
  links: PropTypes.arrayOf(LinkType),
  fullWidth: PropTypes.bool,
}
